import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { Button } from '@/components/ui/button';
import {
  adminFiltersAtom,
  adminAnalysesQueryAtom,
  adminHistoryAuthorsQueryAtom,
  pairsAtom,
} from 'r/atoms';
import { SpinnerCircle } from 'r/ui';
import type { CryptoPair } from 'r/types';
import type { AnalysisData } from './types';
import AdminFilters from './AdminFilters';
import AdminHistoryRow from './AdminHistoryRow';
import ModalBulkVerification from './ModalBulkVerification';
import ModalDeleteAnalysis from './ModalDeleteAnalysis';
import PaginationControls from '../AnalysisManagerTab/PaginationControls';
import useAdminHistoryActions from './useAdminHistoryActions';

const LIMIT_OPTIONS = [20, 50, 100, 200];

const AdminHistory: React.FC = () => {
  const [filters, setFilters] = useAtom(adminFiltersAtom);
  const mainQuery = useAtomValue(adminAnalysesQueryAtom);
  const authorsQuery = useAtomValue(adminHistoryAuthorsQueryAtom);
  const pairsQuery = useAtomValue(pairsAtom);
  const pairs = pairsQuery.data || [];

  const analyses = mainQuery.data?.data || [];
  const total = mainQuery.data?.total || 0;
  const isFetching = mainQuery.isFetching || authorsQuery.isFetching;
  const error = mainQuery.error || authorsQuery.error;
  const isError = mainQuery.isError || authorsQuery.isError;

  // Local UI state
  const [selectedAnalyses, setSelectedAnalyses] = useState<AnalysisData[]>([]);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Actions hook
  const { handleBulkVerification, handleBulkDelete } = useAdminHistoryActions();

  // Clear selections when filters or page change
  const filterKey = JSON.stringify({
    page: filters.page,
    limit: filters.limit,
    author: filters.author,
    symbol: filters.symbol,
    search: filters.search,
    showApiAnalysesOnly: filters.showApiAnalysesOnly,
    timeframe: filters.timeframe,
    performanceFilter: filters.performanceFilter,
  });

  useEffect(() => {
    setSelectedAnalyses([]);
  }, [filterKey]);

  // Data adaptation
  const adaptedAnalyses = useMemo(
    () =>
      analyses.map((analysis) => ({
        id: analysis.id,
        isPredictionUp: analysis.isPredictionUp,
        verifiedBy: analysis.verifiedBy || null,
        pair: pairs.find((p: CryptoPair) => p.symbol === analysis.pair) || {
          symbol: `+++${analysis.pair}+++`,
          logoUrl: '',
        },
        savedAt: analysis.savedAt || analysis.createdAt,
        openDate: analysis.openDate,
        tweetData: {
          ID: analysis.tweetID,
          date: analysis.tweetDate,
          icon: analysis.authorIcon,
          id: analysis.authorUsername,
          name: analysis.authorName,
          text: analysis.tweetText,
          isDeletedFromTweeter: analysis?.deletedFromTweeterAt !== null,
          verifiedBy: analysis.verifiedBy || null,
        },
        performances: {
          performance_1d:
            analysis.performance_1d != null ?
              String(analysis.performance_1d)
            : null,
          performance_1w:
            analysis.performance_1w != null ?
              String(analysis.performance_1w)
            : null,
          performance_2w:
            analysis.performance_2w != null ?
              String(analysis.performance_2w)
            : null,
          performance_1M:
            analysis.performance_1M != null ?
              String(analysis.performance_1M)
            : null,
        },
      })),
    [analyses, pairs],
  );

  const selectedIdsSet = useMemo(
    () => new Set(selectedAnalyses.map((a) => a.id)),
    [selectedAnalyses],
  );

  const handleToggleSelect = useCallback((analysis: AnalysisData) => {
    setSelectedAnalyses((prev) => {
      const isSelected = prev.some((a) => a.id === analysis.id);
      return isSelected ?
          prev.filter((a) => a.id !== analysis.id)
        : [...prev, analysis];
    });
  }, []);

  // Pagination handlers
  const handlePageChange = useCallback(
    (page: number) => {
      setFilters((prev) => ({ ...prev, page }));
    },
    [setFilters],
  );

  const handleLimitChange = useCallback(
    (limit: number) => {
      setFilters((prev) => ({ ...prev, limit, page: 0 }));
    },
    [setFilters],
  );

  if (isError) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center text-(--bearish)">
          <div>Error loading analyses</div>
          <div className="text-sm mt-2">{error?.message}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-7xl">
      {isFetching && (
        <div className="fixed top-4 right-4 z-50 bg-(--focus) text-main px-3 py-1 rounded-lg text-sm">
          Updating...
        </div>
      )}

      <AdminFilters isFetching={isFetching} />

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4">
        <div className="text-sm text-secondary">
          Showing {adaptedAnalyses.length} of {total}
          {selectedAnalyses.length > 0 && (
            <span className="ml-2 text-(--focus) font-medium">
              ({selectedAnalyses.length} selected)
            </span>
          )}
        </div>

        <div className="flex items-center gap-4">
          {selectedAnalyses.length > 0 && (
            <>
              <Button
                variant="destructive"
                onClick={() => setIsDeleteModalOpen(true)}
              >
                <span className="m-auto">
                  Delete ({selectedAnalyses.length})
                </span>
              </Button>
              <Button
                variant="default"
                onClick={() => setIsVerificationModalOpen(true)}
              >
                <span className="m-auto">
                  Verify ({selectedAnalyses.length})
                </span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Cards list */}
      {isFetching && adaptedAnalyses.length === 0 ?
        <div className="flex items-center justify-center py-12">
          <SpinnerCircle color="var(--muted-foreground)" size={32} />
        </div>
      : !isFetching && adaptedAnalyses.length === 0 ?
        <div className="text-center py-8 text-secondary">No analyses found</div>
      : <div className="flex flex-col gap-3 mb-6">
          {adaptedAnalyses.map((analysis) => (
            <AdminHistoryRow
              key={analysis.id}
              analysis={analysis}
              isSelected={selectedIdsSet.has(analysis.id)}
              onToggleSelect={handleToggleSelect}
            />
          ))}
        </div>
      }

      {/* Pagination */}
      <PaginationControls
        page={filters.page}
        total={total}
        limit={filters.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        limitOptions={LIMIT_OPTIONS}
      />

      {/* Modals */}
      <ModalBulkVerification
        isOpen={isVerificationModalOpen}
        selectedAnalyses={selectedAnalyses}
        onClose={() => setIsVerificationModalOpen(false)}
        onConfirm={async (ids) => {
          await handleBulkVerification(ids);
          setSelectedAnalyses([]);
          setIsVerificationModalOpen(false);
        }}
      />

      <ModalDeleteAnalysis
        isOpen={isDeleteModalOpen}
        selectedAnalyses={selectedAnalyses}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={async (ids) => {
          await handleBulkDelete(ids);
          setSelectedAnalyses([]);
          setIsDeleteModalOpen(false);
        }}
      />
    </div>
  );
};

export default AdminHistory;
